import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { formatDateTimeKST } from '../utils/date';
import CalendarView from './admin/CalendarView';
import { useAuthStore } from '../store/authStore';
import { 
  Users, 
  MessageSquare, 
  Clock, 
  BookOpen, 
  TrendingUp,
  Eye,
  Search,
  Calendar,
  Database,
  Activity,
  Shield,
  Crown,
  UserCheck,
  Star,
  Plus,
  X,
  Filter,
  Download,
  RefreshCw,
  AlertCircle,
  KeyRound
} from 'lucide-react';
import crewAPI from '../services/crewAPI-new';

interface User {
  id: string;
  email: string;
  name: string;
  phone?: string;
  role: string;
  isActive: boolean;
  isApproved?: boolean;  // 승인 상태 필드 추가
  loginCount?: number;
  lastLogin?: string;
  provider?: string;
  mustChangePassword?: boolean;
  createdAt: string;
  _count: {
    posts: number;
    prayerLogs: number;
    devotionLogs: number;
  };
}

interface PrayerLog {
  id: string;
  duration: number;
  location?: string;
  content?: string;
  createdAt: string;
  users?: {
    id: string;
    name: string;
    email: string;
  };
}

interface Post {
  id: string;
  content: string;
  verseReference?: string;
  verseText?: string;
  createdAt: string;
  users?: {
    id: string;
    name: string;
    email: string;
  };
  _count: {
    likes: number;
    comments: number;
  };
}

interface Meditation {
  id: string;
  verseReference: string;
  verseText: string;
  step1Complete: boolean;
  step2Complete: boolean;
  step3Complete: boolean;
  step4Complete: boolean;
  createdAt: string;
  users?: {
    id: string;
    name: string;
    email: string;
  };
}

interface Crew {
  id: string;
  name: string;
  description?: string;
  isActive: boolean;
  isOfficial?: boolean;
  maxMembers?: number;
  createdAt: string;
  leader?: {
    id: string;
    name: string;
    email: string;
  };
  _count: {
    members: number;
    joinRequests?: number;
  };
  requestStats?: {
    approved: number;
    pending: number;
    rejected: number;
  };
}

interface Stats {
  totalUsers: number;
  totalPosts: number;
  totalPrayers: number;
  totalMeditations: number;
  totalCrews: number;
  activeUsers: number;
  todayPosts: number;
  todayPrayers: number;
  todayMeditations: number;
  totalPrayerDuration?: number;
  completedMeditationUsers?: number;
}

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';

const AdminDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated, accessToken } = useAuthStore();
  const [activeTab, setActiveTab] = useState('stats');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  
  // 데이터 상태
  const [stats, setStats] = useState<Stats | null>(null);
  const [users, setUsers] = useState<User[]>([]);
  const [prayers, setPrayers] = useState<PrayerLog[]>([]);
  const [posts, setPosts] = useState<Post[]>([]);
  const [meditations, setMeditations] = useState<Meditation[]>([]);
  const [crews, setCrews] = useState<Crew[]>([]);
  const [calendarData, setCalendarData] = useState<any>({});
  const [selectedCrew, setSelectedCrew] = useState<Crew | null>(null);
  const [crewMembers, setCrewMembers] = useState<any>({ approved: [], all: [] });
  const [showAllMembers, setShowAllMembers] = useState(false);
  const [joinRequests, setJoinRequests] = useState<any[]>([]);
  const [joinRequestStatus, setJoinRequestStatus] = useState<'PENDING' | 'APPROVED' | 'REJECTED'>('PENDING');
  const [isLoadingRequests, setIsLoadingRequests] = useState(false);
  const [isLoadingMembers, setIsLoadingMembers] = useState(false);
  const [isMaintaining, setIsMaintaining] = useState(false);
  const [crewsReport, setCrewsReport] = useState<any[] | null>(null);
  
  // 크루 수정/삭제 모달 상태
  const [showCrewEditModal, setShowCrewEditModal] = useState(false);
  const [editingCrew, setEditingCrew] = useState<Crew | null>(null);
  const [editCrewName, setEditCrewName] = useState('');
  const [editCrewDescription, setEditCrewDescription] = useState('');
  const [editCrewMaxMembers, setEditCrewMaxMembers] = useState(50);
  
  // 필터 및 검색 상태
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('all');
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [currentPage, setCurrentPage] = useState(1);

  // 일괄 처리 상태
  const [selectedUsers, setSelectedUsers] = useState<Set<string>>(new Set());
  const [isBulkProcessing, setIsBulkProcessing] = useState(false);

  // 정렬 함수
  const handleSort = (field: string) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
  };

  // 정렬 아이콘 렌더링
  const renderSortIcon = (field: string) => {
    if (sortBy !== field) {
      return <span className="text-gray-400">↕</span>;
    }
    return sortOrder === 'asc' ? <span className="text-blue-600">↑</span> : <span className="text-blue-600">↓</span>;
  };

  // 관리자 권한 확인 (컴포넌트 마운트 시)
  useEffect(() => {
    const checkAdminAccess = async () => {
      setIsCheckingAuth(true);
      
      // 1단계: 인증 확인
      if (!isAuthenticated || !user || !accessToken) {
        console.warn('🚫 관리자 대시보드 접근 거부: 인증되지 않음');
        setIsCheckingAuth(false);
        setIsAuthorized(false);
        setError('관리자 권한이 필요합니다. 먼저 로그인해주세요.');
        setTimeout(() => navigate('/admin-login'), 2000);
        return;
      }

      // 2단계: 관리자 권한 확인
      if (user.role !== 'ADMIN') {
        console.warn('🚫 관리자 대시보드 접근 거부: 권한 없음', { 
          userId: user.id, 
          userRole: user.role 
        });
        setIsCheckingAuth(false);
        setIsAuthorized(false);
        setError('관리자 권한이 필요합니다.');
        setTimeout(() => {
          alert('관리자 권한이 필요합니다.');
          navigate('/');
        }, 2000);
        return;
      }

      // 3단계: 백엔드에서 권한 재확인 (이중 검증)
      try {
        const response = await fetch(`${API_URL}/admin/stats`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          if (response.status === 401) {
            console.warn('🚫 관리자 대시보드 접근 거부: 인증 토큰 무효');
            setIsCheckingAuth(false);
            setIsAuthorized(false);
            setError('인증이 만료되었습니다. 다시 로그인해주세요.');
            setTimeout(() => navigate('/admin-login'), 2000);
            return;
          }
          if (response.status === 403) {
            console.warn('🚫 관리자 대시보드 접근 거부: 관리자 권한 없음');
            setIsCheckingAuth(false);
            setIsAuthorized(false);
            setError('관리자 권한이 필요합니다.');
            setTimeout(() => {
              alert('관리자 권한이 필요합니다.');
              navigate('/');
            }, 2000);
            return;
          }
          throw new Error(`HTTP ${response.status}`);
        }

        // 권한 확인 성공
        console.log('✅ 관리자 대시보드 접근 허용:', { userId: user.id, role: user.role });
        setIsCheckingAuth(false);
        setIsAuthorized(true);
      } catch (error) {
        console.error('❌ 관리자 권한 확인 실패:', error);
        setIsCheckingAuth(false);
        setIsAuthorized(false);
        setError('관리자 권한 확인 중 오류가 발생했습니다.');
        setTimeout(() => {
          alert('관리자 권한 확인 중 오류가 발생했습니다.');
          navigate('/');
        }, 2000);
      }
    };

    checkAdminAccess();
  }, [isAuthenticated, user, accessToken, navigate]);

  // API 호출 함수 (관리자 권한 확인 후에만 사용)
  const fetchData = async (endpoint: string) => {
    if (!isAuthorized || !accessToken) {
      throw new Error('관리자 권한이 필요합니다.');
    }

    const token = accessToken || localStorage.getItem('accessToken');
    console.log(`🔍 어드민 API 호출: ${API_URL}/${endpoint}`);
    console.log(`🔑 토큰 상태:`, token ? '존재함' : '없음');
    
    // 관리자 API는 반드시 토큰 필요
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    };
    
    if (!token) {
      throw new Error('인증 토큰이 필요합니다.');
    }
    
    const response = await fetch(`${API_URL}/${endpoint}`, {
      headers
    });
    
    console.log(`📊 API 응답 상태: ${response.status}`);
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error(`❌ API 오류: ${response.status} - ${errorText}`);
      throw new Error(`HTTP error! status: ${response.status} - ${errorText}`);
    }
    
    const data = await response.json();
    console.log(`✅ API 데이터 수신:`, data);
    return data;
  };

  // 데이터 로드 함수들
  const loadStats = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchData('admin/stats');
      setStats(data);
    } catch (err) {
      console.error('통계 데이터 로딩 실패:', err);
      setError(`통계 데이터를 불러오는데 실패했습니다: ${err instanceof Error ? err.message : '알 수 없는 오류'}`);
    } finally {
      setLoading(false);
    }
  };

  const loadUsers = async () => {
    setLoading(true);
    setError(null);
    try {
      // 모든 데이터를 가져오기 위해 limit을 매우 크게 설정
      const data = await fetchData('admin/users?limit=10000');
      console.log('🔍 사용자 데이터 로딩:', data.users?.length, '명');
      setUsers(data.users || []);
    } catch (err) {
      console.error('사용자 데이터 로딩 실패:', err);
      setError(`사용자 데이터를 불러오는데 실패했습니다: ${err instanceof Error ? err.message : '알 수 없는 오류'}`);
    } finally {
      setLoading(false);
    }
  };

  // 승인 대기 사용자 로드
  const loadPendingUsers = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchData('admin/pending-users');
      console.log('📋 승인 대기 사용자:', data);
      setUsers(data.users || []);
    } catch (err) {
      console.error('승인 대기 사용자 로딩 실패:', err);
      setError(`승인 대기 사용자 데이터를 불러오는데 실패했습니다: ${err instanceof Error ? err.message : '알 수 없는 오류'}`);
    } finally {
      setLoading(false);
    }
  };

  // 사용자 승인/거절 처리
  const handleApproveUser = async (userId: string, action: 'approve' | 'reject') => {
    try {
      setLoading(true);
      
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${API_URL}/admin/approve-user`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          userId,
          action,
          message: `${action === 'approve' ? '승인' : '거절'} 처리되었습니다.`
        })
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      console.log(`✅ 사용자 ${action} 완료:`, data);
      
      // 승인 대기 목록 새로고침
      if (activeTab === 'pending') {
        await loadPendingUsers();
      }
      
      alert(`사용자가 ${action === 'approve' ? '승인' : '거절'}되었습니다.`);
    } catch (err) {
      console.error('사용자 승인/거절 실패:', err);
      alert(`사용자 ${action} 처리에 실패했습니다: ${err instanceof Error ? err.message : '알 수 없는 오류'}`);
    } finally {
      setLoading(false);
    }
  };

  // 사용자 선택 토글
  const toggleUserSelection = (userId: string) => {
    setSelectedUsers(prev => {
      const newSet = new Set(prev);
      if (newSet.has(userId)) {
        newSet.delete(userId);
      } else {
        newSet.add(userId);
      }
      return newSet;
    });
  };

  // 전체 선택/해제
  const toggleSelectAll = () => {
    if (selectedUsers.size === users.length) {
      setSelectedUsers(new Set());
    } else {
      setSelectedUsers(new Set(users.map(user => user.id)));
    }
  };

  // 일괄 승인/거부 처리
  const handleBulkUserAction = async (action: 'approve' | 'reject') => {
    if (selectedUsers.size === 0) {
      alert('처리할 사용자를 선택해주세요.');
      return;
    }

    const actionText = action === 'approve' ? '승인' : '거부';
    if (!confirm(`선택된 ${selectedUsers.size}명의 사용자를 ${actionText}하시겠습니까?`)) {
      return;
    }

    setIsBulkProcessing(true);
    let successCount = 0;
    let failCount = 0;

    try {
      const token = localStorage.getItem('accessToken');
      
      for (const userId of selectedUsers) {
        try {
          const response = await fetch(`${API_URL}/admin/approve-user`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              userId,
              action,
              message: `${actionText} 처리되었습니다.`
            })
          });

          if (response.ok) {
            successCount++;
          } else {
            failCount++;
          }
        } catch (error) {
          console.error(`사용자 ${userId} ${actionText} 실패:`, error);
          failCount++;
        }
      }

      // 결과 메시지
      let message = `${successCount}명의 사용자가 ${actionText}되었습니다.`;
      if (failCount > 0) {
        message += `\n${failCount}명의 처리에 실패했습니다.`;
      }
      alert(message);

      // 선택 초기화 및 데이터 새로고침
      setSelectedUsers(new Set());
      if (activeTab === 'pending') {
        await loadPendingUsers();
      } else if (activeTab === 'users') {
        await loadUsers();
      }

    } catch (err) {
      console.error('일괄 처리 실패:', err);
      alert(`일괄 ${actionText} 처리에 실패했습니다: ${err instanceof Error ? err.message : '알 수 없는 오류'}`);
    } finally {
      setIsBulkProcessing(false);
    }
  };

  // 데이터 내보내기 함수
  const exportData = async (dataType: 'users' | 'prayers' | 'posts' | 'meditations' | 'crews') => {
    try {
      setLoading(true);
      
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${API_URL}/admin/export/${dataType}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${dataType}_export_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      alert(`${dataType} 데이터가 성공적으로 내보내기되었습니다.`);
    } catch (err) {
      console.error('데이터 내보내기 실패:', err);
      alert(`데이터 내보내기에 실패했습니다: ${err instanceof Error ? err.message : '알 수 없는 오류'}`);
    } finally {
      setLoading(false);
    }
  };

  const loadPrayers = async () => {
    setLoading(true);
    try {
      // 모든 데이터를 가져오기 위해 limit을 매우 크게 설정
      const data = await fetchData('admin/prayer-logs?limit=10000');
      console.log('🙏 기도 로그 데이터 로딩:', data.prayerLogs?.length, '개');
      setPrayers(data.prayerLogs || []);
    } catch (err) {
      setError('기도 로그 데이터를 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const loadPosts = async () => {
    setLoading(true);
    try {
      // 모든 데이터를 가져오기 위해 limit을 매우 크게 설정
      const data = await fetchData('admin/posts?limit=10000');
      console.log('📝 게시글 데이터 로딩:', data.posts?.length, '개');
      setPosts(data.posts || []);
    } catch (err) {
      setError('게시글 데이터를 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const loadMeditations = async () => {
    setLoading(true);
    try {
      // 모든 데이터를 가져오기 위해 limit을 매우 크게 설정
      const data = await fetchData('admin/meditations?limit=10000');
      console.log('📖 묵상 기록 데이터 로딩:', data.meditations?.length, '개');
      setMeditations(data.meditations || []);
    } catch (err) {
      setError('묵상 기록 데이터를 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const loadCrews = async () => {
    setLoading(true);
    try {
      // 모든 데이터를 가져오기 위해 limit을 매우 크게 설정
      const data = await fetchData('admin/crews?limit=10000');
      console.log('👥 크루 데이터 로딩:', data.crews?.length, '개');
      setCrews(data.crews || []);
    } catch (err) {
      setError('크루 데이터를 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const loadCalendarData = async (year: number, month: number) => {
    setLoading(true);
    try {
      const data = await fetchData(`admin/calendar-stats?year=${year}&month=${month}`);
      console.log('📅 달력 데이터 로딩:', Object.keys(data).length, '일');
      setCalendarData(data);
    } catch (err) {
      console.error('달력 데이터 로딩 실패:', err);
      setError('달력 데이터를 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };
  
  // 관리자: 전체 크루 리포트 로드
  const loadCrewsReport = async () => {
    try {
      setIsMaintaining(true);
      const data = await fetchData('admin/crews/report');
      setCrewsReport(data.data || []);
      alert(`리포트 불러오기 완료: ${data.data?.length || 0}개 크루`);
    } catch (err) {
      console.error('크루 리포트 로드 실패:', err);
      alert('크루 리포트를 불러오지 못했습니다.');
    } finally {
      setIsMaintaining(false);
    }
  };

  // 관리자: 사용자 역할 변경
  const changeUserRole = async (userId: string, newRole: 'MEMBER' | 'ADMIN') => {
    try {
      if (!confirm(`이 사용자를 ${newRole === 'ADMIN' ? '관리자' : '일반 사용자'}로 변경하시겠습니까?`)) {
        return;
      }

      const token = localStorage.getItem('accessToken');
      const res = await fetch(`${API_URL}/admin/users/${userId}/role`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        body: JSON.stringify({ role: newRole })
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body?.error || res.statusText);
      
      console.log('✅ 사용자 역할 변경 완료:', body.message);
      alert(body.message);
      
      // 사용자 목록 새로고침
      await loadUsers();
    } catch (err) {
      console.error('❌ 사용자 역할 변경 실패:', err);
      alert(`사용자 역할 변경 실패: ${err instanceof Error ? err.message : '알 수 없는 오류'}`);
    }
  };

  const resetUserPassword = async (targetUser: User) => {
    try {
      if (targetUser.role === 'ADMIN') {
        alert('관리자 계정은 이 기능으로 초기화할 수 없습니다.');
        return;
      }

      if (targetUser.provider !== 'email') {
        alert('이메일 로그인 계정만 초기화할 수 있습니다.');
        return;
      }

      if (!confirm(`${targetUser.name}(${targetUser.email}) 비밀번호를 1234로 초기화하시겠습니까?\n\n초기화 후 사용자는 다음 로그인에서 비밀번호 변경이 강제됩니다.`)) {
        return;
      }

      const token = localStorage.getItem('accessToken');
      const res = await fetch(`${API_URL}/admin/users/${targetUser.id}/reset-password`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });

      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body?.error || body?.message || res.statusText);

      alert(`${body.message}\n임시 비밀번호: 1234`);
      await loadUsers();
    } catch (err) {
      console.error('❌ 사용자 비밀번호 초기화 실패:', err);
      alert(`비밀번호 초기화 실패: ${err instanceof Error ? err.message : '알 수 없는 오류'}`);
    }
  };

  // 관리자: 크루 수정 모달 열기
  const openCrewEditModal = (crew: Crew) => {
    setEditingCrew(crew);
    setEditCrewName(crew.name);
    setEditCrewDescription(crew.description || '');
    setEditCrewMaxMembers(crew.maxMembers || 50);
    setShowCrewEditModal(true);
  };

  // 관리자: 크루 수정
  const handleUpdateCrew = async () => {
    if (!editingCrew) return;

    try {
      if (!editCrewName.trim()) {
        alert('크루 이름을 입력해주세요.');
        return;
      }

      const token = localStorage.getItem('accessToken');
      const res = await fetch(`${API_URL}/admin/crews/${editingCrew.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        body: JSON.stringify({
          name: editCrewName,
          description: editCrewDescription,
          maxMembers: editCrewMaxMembers
        })
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body?.error || res.statusText);
      
      console.log('✅ 크루 수정 완료:', body.message);
      alert(body.message);
      
      // 모달 닫기 및 크루 목록 새로고침
      setShowCrewEditModal(false);
      setEditingCrew(null);
      await loadCrews();
    } catch (err) {
      console.error('❌ 크루 수정 실패:', err);
      alert(`크루 수정 실패: ${err instanceof Error ? err.message : '알 수 없는 오류'}`);
    }
  };

  // 관리자: 크루 삭제 (비활성화)
  const handleDeleteCrew = async (crew: Crew) => {
    if (!confirm(`정말 "${crew.name}" 크루를 삭제하시겠습니까?\n\n멤버 ${crew._count.members}명이 이 크루에 속해 있습니다.\n삭제하면 크루가 비활성화됩니다.`)) {
      return;
    }

    try {
      const token = localStorage.getItem('accessToken');
      const res = await fetch(`${API_URL}/admin/crews/${crew.id}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        }
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body?.error || res.statusText);
      
      console.log('✅ 크루 삭제 완료:', body.message);
      alert(body.message);
      
      // 크루 목록 새로고침
      await loadCrews();
    } catch (err) {
      console.error('❌ 크루 삭제 실패:', err);
      alert(`크루 삭제 실패: ${err instanceof Error ? err.message : '알 수 없는 오류'}`);
    }
  };

  // 관리자: 크루 완전 삭제 (영구 삭제)
  const handlePermanentDeleteCrew = async (crew: Crew) => {
    if (!confirm(
      `⚠️ 경고: 이 작업은 되돌릴 수 없습니다!\n\n` +
      `"${crew.name}" 크루를 영구적으로 삭제하시겠습니까?\n\n` +
      `삭제될 데이터:\n` +
      `- 크루 정보\n` +
      `- 멤버 ${crew._count.members}명\n` +
      `- 가입 신청 내역\n\n` +
      `정말 삭제하시겠습니까?`
    )) {
      return;
    }

    // 이중 확인
    if (!confirm(`정말로 "${crew.name}" 크루를 완전히 삭제하시겠습니까?\n\n이 작업은 취소할 수 없습니다!`)) {
      return;
    }

    try {
      const token = localStorage.getItem('accessToken');
      const res = await fetch(`${API_URL}/admin/crews/${crew.id}/permanent`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        }
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body?.error || res.statusText);
      
      console.log('✅ 크루 완전 삭제 완료:', body.message);
      alert(body.message);
      
      await loadCrews();
    } catch (err) {
      console.error('❌ 크루 완전 삭제 실패:', err);
      alert(`크루 완전 삭제 실패: ${err instanceof Error ? err.message : '알 수 없는 오류'}`);
    }
  };

  // 관리자: 크루 공식 권한 토글
  const toggleCrewOfficial = async (crewId: string, isOfficial: boolean) => {
    try {
      const token = localStorage.getItem('accessToken');
      const res = await fetch(`${API_URL}/admin/crews/${crewId}/official`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        body: JSON.stringify({ isOfficial })
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body?.error || res.statusText);
      
      console.log('✅ 공식 크루 권한 변경 완료:', body.message);
      
      // 크루 목록 새로고침
      await loadCrews();
    } catch (err) {
      console.error('❌ 공식 크루 권한 변경 실패:', err);
      alert(`공식 크루 권한 변경 실패: ${err instanceof Error ? err.message : '알 수 없는 오류'}`);
    }
  };

  // 관리자: 레거시 멤버 상태 일괄 정규화(null -> APPROVED)
  const normalizeLegacyMembers = async () => {
    try {
      if (!confirm('레거시 멤버 상태(null)를 APPROVED로 일괄 정규화합니다. 진행할까요?')) return;
      setIsMaintaining(true);
      const token = localStorage.getItem('accessToken');
      const res = await fetch(`${API_URL}/admin/maintenance/normalize-crew-members`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        }
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body?.error || res.statusText);
      alert(`정규화 완료: 업데이트 ${body.updated || 0}건`);
    } catch (err) {
      alert(`정규화 실패: ${err instanceof Error ? err.message : '알 수 없는 오류'}`);
    } finally {
      setIsMaintaining(false);
    }
  };

  // 관리자: 선택 크루 멤버 초기화(리더 유지 + 신청 삭제)
  const clearSelectedCrewMembers = async () => {
    try {
      if (!selectedCrew) return;
      if (!confirm(`'${selectedCrew.name}' 멤버를 리더를 제외하고 모두 삭제하고 신청 내역도 삭제합니다. 진행할까요?`)) return;
      setIsMaintaining(true);
      const token = localStorage.getItem('accessToken');
      const res = await fetch(`${API_URL}/admin/maintenance/clear-crew-members`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        body: JSON.stringify({ crewId: selectedCrew.id, keepLeader: true, clearJoinRequests: true })
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body?.error || res.statusText);
      alert(`초기화 완료: 멤버 ${body.deletedMembers || 0}건, 신청 ${body.clearedRequests || 0}건 삭제`);
      await Promise.all([
        loadCrewMembers(selectedCrew.id),
        loadCrewJoinRequests(selectedCrew.id, 'PENDING')
      ]);
    } catch (err) {
      alert(`초기화 실패: ${err instanceof Error ? err.message : '알 수 없는 오류'}`);
    } finally {
      setIsMaintaining(false);
    }
  };

  // 관리자: 특정 크루 가입 신청 목록 로드
  const loadCrewJoinRequests = async (crewId: string, status: 'PENDING' | 'APPROVED' | 'REJECTED' = 'PENDING') => {
    try {
      setIsLoadingRequests(true);
      const data = await fetchData(`admin/crews/${crewId}/join-requests?status=${status}`);
      setJoinRequests(data.data || []);
    } catch (err) {
      console.error('가입 신청 로드 실패:', err);
      setJoinRequests([]);
    } finally {
      setIsLoadingRequests(false);
    }
  };

  // 관리자: 특정 크루 멤버 목록 로드
  const loadCrewMembers = async (crewId: string) => {
    try {
      setIsLoadingMembers(true);
      const data = await fetchData(`admin/crews/${crewId}/members`);
      setCrewMembers(data.data || { approved: [], all: [] });
    } catch (err) {
      console.error('멤버 목록 로드 실패:', err);
      // 403 등 권한 오류 시 일반 크루 상세 API로 폴백하여 멤버만 표시
      try {
        const detail = await crewAPI.getCrewById(crewId);
        const approvedMembers = (detail.members || []).map((m: any) => ({
          id: m.id,
          user: { id: m.id, name: m.name, email: '', avatar: m.avatar },
          role: m.role,
          status: 'APPROVED',
          joinedAt: m.joinedAt
        }));
        setCrewMembers({ approved: approvedMembers, all: approvedMembers });
      } catch (e) {
        console.error('폴백 멤버 로드 실패:', e);
        setCrewMembers({ approved: [], all: [] });
      }
    } finally {
      setIsLoadingMembers(false);
    }
  };

  // 관리자: 가입 신청 처리
  const handleProcessJoinRequest = async (crewId: string, requestId: string, status: 'APPROVED' | 'REJECTED') => {
    try {
      setIsLoadingRequests(true);
      const token = localStorage.getItem('accessToken');
      const res = await fetch(`${API_URL}/admin/crews/${crewId}/join-requests/${requestId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        body: JSON.stringify({ status })
      });
      if (!res.ok) {
        const text = await res.text();
        if (res.status === 403) {
          throw new Error('권한이 없습니다. 관리자 또는 해당 크루장 계정으로 로그인하세요.');
        }
        throw new Error(`HTTP ${res.status} - ${text}`);
      }
      // 승인 후 목록/멤버 갱신
      await Promise.all([
        loadCrewJoinRequests(crewId, joinRequestStatus),
        loadCrewMembers(crewId)
      ]);
    } catch (err) {
      console.error('가입 신청 처리 실패:', err);
      alert(`가입 신청 처리 실패: ${err instanceof Error ? err.message : '알 수 없는 오류'}`);
    } finally {
      setIsLoadingRequests(false);
    }
  };

  // 탭 변경 시 데이터 로드 (권한 확인 완료 후에만 실행 → 초기 진입 시 race condition 방지)
  useEffect(() => {
    if (!isAuthorized) return;
    switch (activeTab) {
      case 'stats':
        loadStats();
        break;
      case 'users':
        loadUsers();
        break;
      case 'pending':
        loadPendingUsers();
        break;
      case 'prayers':
        loadPrayers();
        break;
      case 'posts':
        loadPosts();
        break;
      case 'meditations':
        loadMeditations();
        break;
      case 'crews':
        loadCrews();
        break;
    }
  }, [activeTab, isAuthorized]);

  // 필터링 및 정렬 함수
  const filterAndSortData = (data: any[]) => {
    let filtered = data;

    // 검색 필터
    if (searchTerm) {
      filtered = filtered.filter(item => 
        Object.values(item).some(value => 
          String(value).toLowerCase().includes(searchTerm.toLowerCase())
        )
      );
    }

    // 역할 필터 (사용자만)
    if (activeTab === 'users' && roleFilter !== 'all') {
      filtered = filtered.filter((user: User) => user.role === roleFilter);
    }

    // 날짜 필터
    if (dateFilter !== 'all') {
      const now = new Date();
      const filterDate = new Date();
      
      switch (dateFilter) {
        case 'today':
          filterDate.setHours(0, 0, 0, 0);
          break;
        case 'week':
          filterDate.setDate(now.getDate() - 7);
          break;
        case 'month':
          filterDate.setMonth(now.getMonth() - 1);
          break;
      }
      
      filtered = filtered.filter((item: any) => 
        new Date(item.createdAt) >= filterDate
      );
    }

    // 정렬
    filtered.sort((a, b) => {
      let aValue, bValue;
      
      // 묵상 탭의 특별한 정렬 처리
      if (activeTab === 'meditations') {
        switch (sortBy) {
          case 'user':
            aValue = a.users?.name || '';
            bValue = b.users?.name || '';
            break;
          case 'verseReference':
            aValue = a.verseReference || '';
            bValue = b.verseReference || '';
            break;
          case 'step1Complete':
            aValue = a.step1Complete ? 1 : 0;
            bValue = b.step1Complete ? 1 : 0;
            break;
          case 'step2Complete':
            aValue = a.step2Complete ? 1 : 0;
            bValue = b.step2Complete ? 1 : 0;
            break;
          case 'step3Complete':
            aValue = a.step3Complete ? 1 : 0;
            bValue = b.step3Complete ? 1 : 0;
            break;
          case 'step4Complete':
            aValue = a.step4Complete ? 1 : 0;
            bValue = b.step4Complete ? 1 : 0;
            break;
          default:
            aValue = a[sortBy];
            bValue = b[sortBy];
        }
      } else if (activeTab === 'crews') {
        // 크루 탭의 특별한 정렬 처리
        switch (sortBy) {
          case 'members':
            aValue = a._count?.members || 0;
            bValue = b._count?.members || 0;
            break;
          default:
            aValue = a[sortBy];
            bValue = b[sortBy];
        }
      } else if (activeTab === 'prayers') {
        // 기도 탭의 특별한 정렬 처리
        switch (sortBy) {
          case 'user':
            aValue = a.users?.name || '';
            bValue = b.users?.name || '';
            break;
          default:
            aValue = a[sortBy];
            bValue = b[sortBy];
        }
      } else if (activeTab === 'posts') {
        // 게시글 탭의 특별한 정렬 처리
        switch (sortBy) {
          case 'user':
            aValue = a.users?.name || '';
            bValue = b.users?.name || '';
            break;
          case 'likes':
            aValue = a._count?.likes || 0;
            bValue = b._count?.likes || 0;
            break;
          case 'comments':
            aValue = a._count?.comments || 0;
            bValue = b._count?.comments || 0;
            break;
          default:
            aValue = a[sortBy];
            bValue = b[sortBy];
        }
      } else {
        aValue = a[sortBy];
        bValue = b[sortBy];
      }
      
      // 날짜 필드 처리
      if (sortBy === 'createdAt' || sortBy === 'updatedAt') {
        aValue = new Date(aValue).getTime();
        bValue = new Date(bValue).getTime();
      }
      
      // 문자열 필드 처리
      if (typeof aValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }
      
      if (sortOrder === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

    return filtered;
  };

  // 페이지네이션
  const getPaginatedData = (data: any[]) => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return data.slice(startIndex, endIndex);
  };

  // 현재 데이터 가져오기
  const getCurrentData = () => {
    switch (activeTab) {
      case 'users': return users;
      case 'prayers': return prayers;
      case 'posts': return posts;
      case 'meditations': return meditations;
      case 'crews': return crews;
      default: return [];
    }
  };

  const currentData = getCurrentData();
  const filteredData = filterAndSortData(currentData);
  const paginatedData = getPaginatedData(filteredData);
  const totalPages = Math.ceil(filteredData.length / itemsPerPage);

  // 시간을 시:분 형식으로 변환하는 함수
  const formatDuration = (seconds: number): string => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    if (hours > 0) {
      return `${hours}시간 ${minutes}분`;
    }
    return `${minutes}분`;
  };

  // 통계 카드 컴포넌트 - 새로운 디자인
  const StatCard = ({ title, value, icon: Icon, gradient, trend, trendValue, subtitle }: any) => {
    const safeValue = value ?? 0;
    return (
      <div className="group relative bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden border border-gray-100">
        {/* 그라디언트 배경 */}
        <div className={`absolute inset-0 ${gradient} opacity-5 group-hover:opacity-10 transition-opacity`}></div>
        
        {/* 컨텐츠 */}
        <div className="relative p-6">
          <div className="flex items-start justify-between mb-4">
            <div className={`p-3 rounded-xl ${gradient} bg-opacity-10`}>
              <Icon className="w-7 h-7 text-white" style={{ filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))' }} />
            </div>
            {trend && (
              <div className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold ${
                trend === 'up' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
              }`}>
                <TrendingUp className={`w-3 h-3 ${trend === 'down' ? 'rotate-180' : ''}`} />
                {trendValue}
              </div>
            )}
          </div>
          
        <div>
            <p className="text-sm text-gray-600 mb-1 font-medium">{title}</p>
            <p className="text-3xl font-bold text-gray-900 tracking-tight">{safeValue.toLocaleString()}</p>
            {subtitle && (
              <p className="text-xs text-gray-500 mt-1">{subtitle}</p>
            )}
        </div>
      </div>
        
        {/* 하단 장식선 */}
        <div className={`h-1 ${gradient}`}></div>
    </div>
  );
  };

  // 테이블 헤더 컴포넌트 - 개선된 스타일
  const TableHeader = ({ children }: any) => (
    <thead className="bg-gradient-to-r from-gray-100 to-gray-50 border-b-2 border-gray-200">
      <tr className="text-xs font-semibold text-gray-700 uppercase tracking-wider">
        {children}
      </tr>
    </thead>
  );

  // 테이블 셀 컴포넌트 - 개선된 스타일
  const TableCell = ({ children, className = "" }: any) => (
    <td className={`px-4 py-3 text-sm ${className}`}>
      {children}
    </td>
  );

  // 권한 확인 중
  if (isCheckingAuth) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 mb-4">
            <Shield className="w-16 h-16 text-blue-600 animate-pulse" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">권한 확인 중...</h2>
          <p className="text-gray-600">관리자 권한을 확인하고 있습니다.</p>
        </div>
      </div>
    );
  }

  // 권한 없음
  if (!isAuthorized) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-4">
          <div className="inline-flex items-center justify-center w-16 h-16 mb-4 bg-red-100 rounded-full">
            <AlertCircle className="w-8 h-8 text-red-600" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">접근 권한이 없습니다</h2>
          <p className="text-gray-600 mb-4">{error || '관리자 권한이 필요한 페이지입니다.'}</p>
          <div className="flex gap-2 justify-center">
            <button
              onClick={() => navigate('/')}
              className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
            >
              메인으로
            </button>
            <button
              onClick={() => navigate('/admin-login')}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              관리자 로그인
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50 to-indigo-50 p-4 md:p-6">
      <div className="max-w-7xl mx-auto">
        {/* 헤더 - 새로운 디자인 */}
        <div className="bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 rounded-3xl shadow-2xl p-6 mb-6 relative overflow-hidden">
          {/* 배경 장식 */}
          <div className="absolute top-0 right-0 w-64 h-64 bg-white opacity-5 rounded-full -mr-32 -mt-32"></div>
          <div className="absolute bottom-0 left-0 w-48 h-48 bg-white opacity-5 rounded-full -ml-24 -mb-24"></div>
          
          <div className="relative flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <div className="p-3 bg-white/20 rounded-2xl backdrop-blur-sm">
                  <Database className="w-8 h-8 text-white" />
                </div>
                <div>
                  <h1 className="text-3xl font-bold text-white mb-1">
                    e2b 관리자 대시보드
          </h1>
                  <p className="text-blue-100 text-sm font-medium">실시간 데이터 모니터링 및 관리</p>
            </div>
              </div>
            </div>
            
            <div className="flex flex-wrap items-center gap-2">
              <button
                onClick={() => window.location.reload()}
                className="flex items-center gap-2 px-4 py-2 bg-white text-blue-600 rounded-xl hover:bg-blue-50 transition-all shadow-lg font-semibold hover:scale-105"
              >
                <RefreshCw className="w-4 h-4" />
                <span className="hidden sm:inline">새로고침</span>
              </button>
              {activeTab === 'crews' && (
                <>
                  <button
                    onClick={normalizeLegacyMembers}
                    disabled={isMaintaining}
                    className="flex items-center gap-2 px-4 py-2 bg-amber-500 text-white rounded-xl hover:bg-amber-600 disabled:opacity-50 transition-all shadow-lg font-semibold hover:scale-105"
                  >
                    <Database className="w-4 h-4" />
                    <span className="hidden sm:inline">레거시 정규화</span>
                  </button>
                  <button
                    onClick={loadCrewsReport}
                    disabled={isMaintaining}
                    className="flex items-center gap-2 px-4 py-2 bg-gray-700 text-white rounded-xl hover:bg-gray-800 disabled:opacity-50 transition-all shadow-lg font-semibold hover:scale-105"
                  >
                    <Activity className="w-4 h-4" />
                    <span className="hidden sm:inline">크루 리포트</span>
                  </button>
                </>
              )}
            </div>
          </div>
        </div>

        {/* 탭 네비게이션 - 새로운 디자인 */}
        <div className="bg-white rounded-2xl shadow-lg mb-6 p-2">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <nav className="flex flex-wrap gap-2">
            {[
              { id: 'stats', label: '대시보드', icon: TrendingUp, gradient: 'from-blue-500 to-indigo-600' },
              { id: 'calendar', label: '활동 달력', icon: Calendar, gradient: 'from-emerald-500 to-teal-600' },
              { id: 'users', label: '사용자', icon: Users, gradient: 'from-emerald-500 to-teal-600' },
              { id: 'pending', label: '승인대기', icon: UserCheck, gradient: 'from-amber-500 to-orange-600' },
              { id: 'prayers', label: '기도', icon: Clock, gradient: 'from-purple-500 to-pink-600' },
              { id: 'posts', label: '게시글', icon: MessageSquare, gradient: 'from-cyan-500 to-blue-600' },
              { id: 'meditations', label: '묵상', icon: BookOpen, gradient: 'from-rose-500 to-red-600' },
              { id: 'crews', label: '크루', icon: Shield, gradient: 'from-violet-500 to-purple-600' }
            ].map(({ id, label, icon: Icon, gradient }) => (
                <button
                  key={id}
                  onClick={() => setActiveTab(id)}
                className={`group relative flex items-center gap-2 px-4 py-3 rounded-xl font-semibold text-sm transition-all duration-300 ${
                    activeTab === id
                    ? `bg-gradient-to-r ${gradient} text-white shadow-lg scale-105`
                    : 'bg-gray-50 text-gray-600 hover:bg-gray-100 hover:scale-102'
                }`}
              >
                <Icon className={`w-5 h-5 transition-transform group-hover:scale-110 ${
                  activeTab === id ? '' : 'opacity-70'
                }`} />
                <span>{label}</span>
                {activeTab === id && (
                  <div className="absolute -bottom-1 left-1/2 transform -translate-x-1/2 w-8 h-1 bg-white rounded-full shadow-md"></div>
                )}
                </button>
              ))}
            </nav>
            
            {/* 데이터 내보내기 버튼 */}
            {activeTab !== 'stats' && activeTab !== 'calendar' && (
              <div className="flex items-center gap-2">
                <button
                  onClick={() => exportData(activeTab as 'users' | 'prayers' | 'posts' | 'meditations' | 'crews')}
                  disabled={loading}
                  className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-xl hover:bg-green-700 disabled:opacity-50 transition-all shadow-lg font-semibold hover:scale-105"
                >
                  <Download className="w-4 h-4" />
                  <span className="hidden sm:inline">데이터 내보내기</span>
                </button>
              </div>
            )}
          </div>
        </div>

        {/* 필터 및 검색 - 개선된 디자인 */}
        {activeTab !== 'stats' && (
          <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 mb-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {/* 검색 */}
              <div className="relative group">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 group-focus-within:text-blue-600 transition-colors" />
                <input
                  type="text"
                  placeholder="검색..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-12 pr-4 py-3 text-sm border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all outline-none hover:border-gray-300"
                />
              </div>

              {/* 역할 필터 (사용자만) */}
              {activeTab === 'users' && (
                <div className="relative">
                  <Filter className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
                <select
                  value={roleFilter}
                  onChange={(e) => setRoleFilter(e.target.value)}
                    className="w-full pl-12 pr-4 py-3 text-sm border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all outline-none hover:border-gray-300 appearance-none bg-white cursor-pointer font-medium"
                >
                  <option value="all">모든 역할</option>
                  <option value="ADMIN">관리자</option>
                  <option value="PASTOR">목사</option>
                  <option value="LEADER">리더</option>
                  <option value="MEMBER">회원</option>
                </select>
                </div>
              )}

              {/* 날짜 필터 */}
              <div className="relative">
                <Calendar className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
              <select
                value={dateFilter}
                onChange={(e) => setDateFilter(e.target.value)}
                  className="w-full pl-12 pr-4 py-3 text-sm border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all outline-none hover:border-gray-300 appearance-none bg-white cursor-pointer font-medium"
              >
                <option value="all">전체 기간</option>
                <option value="today">오늘</option>
                <option value="week">최근 1주</option>
                <option value="month">최근 1개월</option>
              </select>
              </div>

              {/* 정렬 */}
              <div className="flex gap-2">
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="flex-1 px-4 py-3 text-sm border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all outline-none hover:border-gray-300 appearance-none bg-white cursor-pointer font-medium"
                >
                  <option value="createdAt">생성일</option>
                  <option value="name">이름</option>
                  <option value="email">이메일</option>
                </select>
                <button
                  onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                  className="px-4 py-3 text-sm bg-gradient-to-r from-blue-500 to-indigo-600 text-white border-2 border-transparent rounded-xl hover:from-blue-600 hover:to-indigo-700 transition-all font-bold shadow-md hover:shadow-lg"
                >
                  {sortOrder === 'asc' ? '↑' : '↓'}
                </button>
              </div>
            </div>

            {/* 페이지 크기 및 페이지네이션 - 개선된 디자인 */}
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between mt-6 gap-4">
              <div className="flex items-center gap-3">
                <span className="text-sm text-gray-600 font-medium">페이지당:</span>
                <select
                  value={itemsPerPage}
                  onChange={(e) => {
                    const value = Number(e.target.value);
                    setItemsPerPage(value);
                    setCurrentPage(1);
                  }}
                  className="px-4 py-2 text-sm border-2 border-gray-200 rounded-xl font-semibold focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all outline-none hover:border-gray-300 appearance-none bg-white cursor-pointer"
                >
                  <option value={10}>10개</option>
                  <option value={20}>20개</option>
                  <option value={50}>50개</option>
                  <option value={100}>100개</option>
                  <option value={500}>500개</option>
                  <option value={1000}>1000개</option>
                  <option value={999999}>전체 보기</option>
                </select>
                <div className="flex items-center gap-2 text-sm">
                  <span className="text-gray-600">총</span>
                  <span className="font-bold text-blue-600 text-base">{filteredData.length}</span>
                  <span className="text-gray-600">개 중</span>
                  <span className="font-bold text-emerald-600 text-base">{paginatedData.length}</span>
                  <span className="text-gray-600">개 표시</span>
                </div>
              </div>

              {/* 페이지네이션 */}
              {itemsPerPage < 999999 && (
                <div className="flex items-center gap-2">
                <button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                    className="px-4 py-2 text-sm bg-white hover:bg-gray-50 border-2 border-gray-200 rounded-xl disabled:opacity-50 disabled:cursor-not-allowed font-semibold transition-all hover:border-gray-300 hover:shadow-md disabled:hover:shadow-none"
                >
                  이전
                </button>
                  <div className="px-4 py-2 bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-xl font-bold text-sm shadow-md">
                    {currentPage} / {totalPages || 1}
                  </div>
                <button
                  onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                    disabled={currentPage === totalPages || totalPages === 0}
                    className="px-4 py-2 text-sm bg-white hover:bg-gray-50 border-2 border-gray-200 rounded-xl disabled:opacity-50 disabled:cursor-not-allowed font-semibold transition-all hover:border-gray-300 hover:shadow-md disabled:hover:shadow-none"
                >
                  다음
                </button>
              </div>
              )}
            </div>
          </div>
        )}

        {/* 로딩 및 에러 상태 - 개선된 디자인 */}
        {loading && (
          <div className="bg-white rounded-2xl shadow-lg p-12 text-center border border-gray-100">
            <div className="relative w-16 h-16 mx-auto mb-4">
              <div className="absolute inset-0 border-4 border-blue-200 rounded-full"></div>
              <div className="absolute inset-0 border-4 border-blue-600 rounded-full border-t-transparent animate-spin"></div>
            </div>
            <p className="text-lg font-semibold text-gray-700 mb-1">데이터를 불러오는 중...</p>
            <p className="text-sm text-gray-500">잠시만 기다려주세요</p>
          </div>
        )}

        {error && (
          <div className="bg-gradient-to-r from-red-50 to-pink-50 border-2 border-red-200 rounded-2xl p-6 mb-6 shadow-lg">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-red-100 rounded-full">
                <X className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-red-800 mb-1">오류 발생</h3>
                <p className="text-sm text-red-700">{error}</p>
              </div>
            </div>
          </div>
        )}

        {/* 활동 달력 */}
        {activeTab === 'calendar' && (
          <CalendarView 
            calendarData={calendarData}
            onMonthChange={loadCalendarData}
          />
        )}

        {/* 통계 대시보드 - 새로운 디자인 */}
            {activeTab === 'stats' && stats && (
          <div className="space-y-6">
            {/* 주요 지표 */}
            <div>
              <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center gap-2">
                <TrendingUp className="w-6 h-6 text-blue-600" />
                주요 지표
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <StatCard 
                  title="총 사용자" 
                  value={stats.totalUsers} 
                  icon={Users} 
                  gradient="bg-gradient-to-br from-blue-500 to-indigo-600"
                  trend="up"
                  trendValue="+12%"
                />
                <StatCard 
                  title="활성 사용자" 
                  value={stats.activeUsers} 
                  icon={Activity} 
                  gradient="bg-gradient-to-br from-emerald-500 to-teal-600"
                  trend="up"
                  trendValue="+8%"
                />
                <StatCard 
                  title="총 크루" 
                  value={stats.totalCrews} 
                  icon={Shield} 
                  gradient="bg-gradient-to-br from-violet-500 to-purple-600"
                  trend="up"
                  trendValue="+5%"
                />
                <StatCard 
                  title="총 게시글" 
                  value={stats.totalPosts} 
                  icon={MessageSquare} 
                  gradient="bg-gradient-to-br from-cyan-500 to-blue-600"
                />
              </div>
            </div>

            {/* 활동 지표 */}
            <div>
              <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center gap-2">
                <Activity className="w-6 h-6 text-emerald-600" />
                활동 현황
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <StatCard 
                  title="총 기도" 
                  value={stats.totalPrayers} 
                  icon={Clock} 
                  gradient="bg-gradient-to-br from-purple-500 to-pink-600"
                  subtitle={stats.totalPrayerDuration ? formatDuration(stats.totalPrayerDuration) : '0분'}
                />
                <StatCard 
                  title="총 묵상" 
                  value={stats.totalMeditations} 
                  icon={BookOpen} 
                  gradient="bg-gradient-to-br from-rose-500 to-red-600"
                  subtitle={stats.completedMeditationUsers ? `${stats.completedMeditationUsers}명 완료` : '0명 완료'}
                />
                <StatCard 
                  title="오늘 게시글" 
                  value={stats.todayPosts} 
                  icon={Calendar} 
                  gradient="bg-gradient-to-br from-amber-500 to-orange-600"
                  trend="up"
                  trendValue="+15%"
                />
                <StatCard 
                  title="오늘 기도" 
                  value={stats.todayPrayers} 
                  icon={Clock} 
                  gradient="bg-gradient-to-br from-pink-500 to-rose-600"
                />
              </div>
            </div>

            {/* 요약 섹션 */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* 사용자 활동 요약 */}
              <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100">
                <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
                  <Users className="w-5 h-5 text-blue-600" />
                  사용자 활동 요약
                </h3>
                <div className="space-y-4">
                  <div className="flex justify-between items-center p-3 bg-blue-50 rounded-xl">
                    <span className="text-sm font-medium text-gray-700">활성율</span>
                    <span className="text-xl font-bold text-blue-600">
                      {(stats.totalUsers ?? 0) > 0 ? Math.round(((stats.activeUsers ?? 0) / stats.totalUsers) * 100) : 0}%
                    </span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-emerald-50 rounded-xl">
                    <span className="text-sm font-medium text-gray-700">평균 활동</span>
                    <span className="text-xl font-bold text-emerald-600">
                      {(stats.totalUsers ?? 0) > 0 ? Math.round(((stats.totalPosts ?? 0) + (stats.totalPrayers ?? 0)) / stats.totalUsers) : 0}
                    </span>
                  </div>
                </div>
              </div>

              {/* 컨텐츠 요약 */}
              <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100">
                <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
                  <Activity className="w-5 h-5 text-emerald-600" />
                  컨텐츠 현황
                </h3>
                <div className="space-y-3">
                  {[
                    { label: '게시글', value: stats.totalPosts ?? 0, color: 'bg-cyan-500', width: '75%' },
                    { label: '기도', value: stats.totalPrayers ?? 0, color: 'bg-purple-500', width: '90%' },
                    { label: '묵상', value: stats.totalMeditations ?? 0, color: 'bg-rose-500', width: '85%' }
                  ].map((item, idx) => (
                    <div key={idx}>
                      <div className="flex justify-between text-sm mb-1">
                        <span className="font-medium text-gray-700">{item.label}</span>
                        <span className="font-bold text-gray-900">{item.value.toLocaleString()}</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div 
                          className={`${item.color} h-2 rounded-full transition-all duration-500`}
                          style={{ width: item.width }}
                        ></div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
              </div>
            )}

        {/* 데이터 테이블 - 개선된 디자인 */}
        {activeTab !== 'stats' && !loading && (
          <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
            {/* 일괄 처리 버튼 (사용자 탭만) */}
            {activeTab === 'users' && selectedUsers.size > 0 && (
              <div className="bg-blue-50 border-b border-blue-200 px-6 py-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <span className="text-sm font-medium text-blue-800">
                      {selectedUsers.size}명 선택됨
                    </span>
                    <button
                      onClick={() => setSelectedUsers(new Set())}
                      className="text-sm text-blue-600 hover:text-blue-800 underline"
                    >
                      선택 해제
                    </button>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleBulkUserAction('approve')}
                      disabled={isBulkProcessing}
                      className="px-4 py-2 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                    >
                      {isBulkProcessing ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                          처리 중...
                        </>
                      ) : (
                        <>
                          <UserCheck size={16} />
                          일괄 승인
                        </>
                      )}
                    </button>
                    <button
                      onClick={() => handleBulkUserAction('reject')}
                      disabled={isBulkProcessing}
                      className="px-4 py-2 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                    >
                      <X size={16} />
                      일괄 거부
                    </button>
                  </div>
                </div>
              </div>
            )}
            
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
            {activeTab === 'users' && (
                  <>
                    <TableHeader>
                      <th className="px-2 py-2 text-left">
                        <input
                          type="checkbox"
                          checked={selectedUsers.size === users.length && users.length > 0}
                          onChange={toggleSelectAll}
                          className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                        />
                      </th>
                      <th 
                        className="px-2 py-2 text-left cursor-pointer hover:bg-gray-50 select-none"
                        onClick={() => handleSort('name')}
                      >
                        <div className="flex items-center gap-1">
                          이름
                          {renderSortIcon('name')}
                        </div>
                      </th>
                      <th 
                        className="px-2 py-2 text-left cursor-pointer hover:bg-gray-50 select-none"
                        onClick={() => handleSort('email')}
                      >
                        <div className="flex items-center gap-1">
                          이메일
                          {renderSortIcon('email')}
                        </div>
                      </th>
                      <th 
                        className="px-2 py-2 text-left cursor-pointer hover:bg-gray-50 select-none"
                        onClick={() => handleSort('role')}
                      >
                        <div className="flex items-center gap-1">
                          역할
                          {renderSortIcon('role')}
                        </div>
                      </th>
                      <th 
                        className="px-2 py-2 text-left cursor-pointer hover:bg-gray-50 select-none"
                        onClick={() => handleSort('loginCount')}
                      >
                        <div className="flex items-center gap-1">
                          로그인 수
                          {renderSortIcon('loginCount')}
                        </div>
                      </th>
                      <th 
                        className="px-2 py-2 text-left cursor-pointer hover:bg-gray-50 select-none"
                        onClick={() => handleSort('createdAt')}
                      >
                        <div className="flex items-center gap-1">
                          생성일
                          {renderSortIcon('createdAt')}
                        </div>
                      </th>
                      <th 
                        className="px-2 py-2 text-left cursor-pointer hover:bg-gray-50 select-none"
                        onClick={() => handleSort('isApproved')}
                      >
                        <div className="flex items-center gap-1">
                          승인상태
                          {renderSortIcon('isApproved')}
                        </div>
                      </th>
                      <th className="px-2 py-2 text-left">액션</th>
                    </TableHeader>
                    <tbody>
                      {paginatedData.map((user: any) => (
                        <tr key={user.id} className="border-b border-gray-100 hover:bg-gray-50">
                          <TableCell>
                            <input
                              type="checkbox"
                              checked={selectedUsers.has(user.id)}
                              onChange={() => toggleUserSelection(user.id)}
                              className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                            />
                          </TableCell>
                          <TableCell className="font-medium">{user.name}</TableCell>
                          <TableCell>{user.email}</TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1">
                              <span className={`px-2 py-1 text-xs rounded ${
                                user.role === 'ADMIN' ? 'bg-red-100 text-red-800' :
                                user.role === 'PASTOR' ? 'bg-purple-100 text-purple-800' :
                                user.role === 'LEADER' ? 'bg-blue-100 text-blue-800' :
                                'bg-gray-100 text-gray-800'
                              }`}>
                                {user.role}
                              </span>
                              {user.mustChangePassword && (
                                <span className="px-2 py-1 text-xs rounded bg-amber-100 text-amber-800">
                                  비번변경필수
                                </span>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>{user.loginCount || 0}</TableCell>
                          <TableCell>{formatDateTimeKST(new Date(user.createdAt))}</TableCell>
                          <TableCell>
                            <span className={`px-2 py-1 text-xs rounded ${
                              user.isApproved ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                            }`}>
                              {user.isApproved ? '승인됨' : '대기중'}
                            </span>
                          </TableCell>
                          <TableCell>
                            <div className="flex space-x-2">
                              {user.role !== 'ADMIN' ? (
                                <button
                                  onClick={() => changeUserRole(user.id, 'ADMIN')}
                                  className="px-2 py-1 text-white text-xs rounded bg-red-500 hover:bg-red-600"
                                  title="관리자로 승급"
                                >
                                  <Shield className="inline w-3 h-3 mr-1" />
                                  관리자
                                </button>
                              ) : (
                                <button
                                  onClick={() => changeUserRole(user.id, 'MEMBER')}
                                  className="px-2 py-1 text-white text-xs rounded bg-gray-500 hover:bg-gray-600"
                                  title="일반 사용자로 변경"
                                >
                                  일반
                                </button>
                              )}
                              <button
                                onClick={() => handleApproveUser(user.id, 'approve')}
                                className={`px-2 py-1 text-white text-xs rounded hover:opacity-80 ${
                                  user.isApproved 
                                    ? 'bg-green-600 hover:bg-green-700' 
                                    : 'bg-green-500 hover:bg-green-600'
                                }`}
                              >
                                {user.isApproved ? '재승인' : '승인'}
                              </button>
                              <button
                                onClick={() => handleApproveUser(user.id, 'reject')}
                                className={`px-2 py-1 text-white text-xs rounded hover:opacity-80 ${
                                  !user.isApproved 
                                    ? 'bg-red-600 hover:bg-red-700' 
                                    : 'bg-red-500 hover:bg-red-600'
                                }`}
                              >
                                {!user.isApproved ? '거절' : '승인취소'}
                              </button>
                              <button
                                onClick={() => resetUserPassword(user)}
                                disabled={user.role === 'ADMIN' || user.provider !== 'email'}
                                className="px-2 py-1 text-white text-xs rounded bg-indigo-500 hover:bg-indigo-600 disabled:bg-gray-300 disabled:cursor-not-allowed"
                                title="임시 비밀번호 1234로 초기화"
                              >
                                <KeyRound className="inline w-3 h-3 mr-1" />
                                비번초기화
                              </button>
                            </div>
                          </TableCell>
                        </tr>
                      ))}
                    </tbody>
                  </>
            )}

            {activeTab === 'pending' && (
                  <>
                    {console.log('🔍 승인대기 탭 - paginatedData:', paginatedData)}
                    {console.log('🔍 승인대기 탭 - users:', users)}
                    {paginatedData.length > 0 ? (
                      <>
                        <TableHeader>
                          <th 
                            className="px-2 py-2 text-left cursor-pointer hover:bg-gray-50 select-none"
                            onClick={() => handleSort('name')}
                          >
                            <div className="flex items-center gap-1">
                              이름
                              {renderSortIcon('name')}
                            </div>
                          </th>
                          <th 
                            className="px-2 py-2 text-left cursor-pointer hover:bg-gray-50 select-none"
                            onClick={() => handleSort('email')}
                          >
                            <div className="flex items-center gap-1">
                              이메일
                              {renderSortIcon('email')}
                            </div>
                          </th>
                          <th 
                            className="px-2 py-2 text-left cursor-pointer hover:bg-gray-50 select-none"
                            onClick={() => handleSort('realName')}
                          >
                            <div className="flex items-center gap-1">
                              실명
                              {renderSortIcon('realName')}
                            </div>
                          </th>
                          <th 
                            className="px-2 py-2 text-left cursor-pointer hover:bg-gray-50 select-none"
                            onClick={() => handleSort('churchName')}
                          >
                            <div className="flex items-center gap-1">
                              교회
                              {renderSortIcon('churchName')}
                            </div>
                          </th>
                          <th 
                            className="px-2 py-2 text-left cursor-pointer hover:bg-gray-50 select-none"
                            onClick={() => handleSort('createdAt')}
                          >
                            <div className="flex items-center gap-1">
                              가입일
                              {renderSortIcon('createdAt')}
                            </div>
                          </th>
                          <th className="px-2 py-2 text-left">액션</th>
                        </TableHeader>
                        <tbody>
                          {paginatedData.map((user: any) => (
                            <tr key={user.id} className="border-b border-gray-100 hover:bg-gray-50">
                              <TableCell className="font-medium">{user.name}</TableCell>
                              <TableCell>{user.email}</TableCell>
                              <TableCell>{user.realName || '-'}</TableCell>
                              <TableCell>{user.churchName || '-'}</TableCell>
                              <TableCell>{formatDateTimeKST(new Date(user.createdAt))}</TableCell>
                              <TableCell>
                                <div className="flex space-x-2">
                                  <button
                                    onClick={() => handleApproveUser(user.id, 'approve')}
                                    className={`px-2 py-1 text-white text-xs rounded hover:opacity-80 ${
                                      user.isApproved 
                                        ? 'bg-green-600 hover:bg-green-700' 
                                        : 'bg-green-500 hover:bg-green-600'
                                    }`}
                                  >
                                    {user.isApproved ? '재승인' : '승인'}
                                  </button>
                                  <button
                                    onClick={() => handleApproveUser(user.id, 'reject')}
                                    className={`px-2 py-1 text-white text-xs rounded hover:opacity-80 ${
                                      !user.isApproved 
                                        ? 'bg-red-600 hover:bg-red-700' 
                                        : 'bg-red-500 hover:bg-red-600'
                                    }`}
                                  >
                                    {!user.isApproved ? '거절' : '승인취소'}
                                  </button>
                                </div>
                              </TableCell>
                            </tr>
                          ))}
                        </tbody>
                      </>
                    ) : (
                      <div className="text-center py-12">
                        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                          <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                          </svg>
                        </div>
                        <h3 className="text-lg font-medium text-gray-900 mb-2">승인 대기 사용자가 없습니다</h3>
                        <p className="text-gray-500 mb-4">
                          현재 모든 사용자가 자동 승인되어 대기 중인 사용자가 없습니다.
                        </p>
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 max-w-md mx-auto">
                          <div className="flex items-start">
                            <div className="flex-shrink-0">
                              <span className="text-blue-500 text-lg">ℹ️</span>
                            </div>
                            <div className="ml-3">
                              <p className="text-blue-800 text-sm">
                                <strong>자동 승인 시스템:</strong> 새로 가입하는 사용자는 자동으로 승인됩니다.
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </>
            )}

            {activeTab === 'prayers' && (
                  <>
                    <TableHeader>
                      <th 
                        className="px-2 py-2 text-left cursor-pointer hover:bg-gray-50 select-none"
                        onClick={() => handleSort('user')}
                      >
                        <div className="flex items-center gap-1">
                          사용자
                          {renderSortIcon('user')}
                        </div>
                      </th>
                      <th 
                        className="px-2 py-2 text-left cursor-pointer hover:bg-gray-50 select-none"
                        onClick={() => handleSort('duration')}
                      >
                        <div className="flex items-center gap-1">
                          시간(분)
                          {renderSortIcon('duration')}
                        </div>
                      </th>
                      <th 
                        className="px-2 py-2 text-left cursor-pointer hover:bg-gray-50 select-none"
                        onClick={() => handleSort('location')}
                      >
                        <div className="flex items-center gap-1">
                          위치
                          {renderSortIcon('location')}
                        </div>
                      </th>
                      <th 
                        className="px-2 py-2 text-left cursor-pointer hover:bg-gray-50 select-none"
                        onClick={() => handleSort('content')}
                      >
                        <div className="flex items-center gap-1">
                          내용
                          {renderSortIcon('content')}
                        </div>
                      </th>
                      <th 
                        className="px-2 py-2 text-left cursor-pointer hover:bg-gray-50 select-none"
                        onClick={() => handleSort('createdAt')}
                      >
                        <div className="flex items-center gap-1">
                          생성일
                          {renderSortIcon('createdAt')}
                        </div>
                      </th>
                    </TableHeader>
                    <tbody>
                      {paginatedData.map((prayer: PrayerLog) => (
                        <tr key={prayer.id} className="border-b border-gray-100 hover:bg-gray-50">
                          <TableCell className="font-medium">{prayer.users?.name || '익명'}</TableCell>
                          <TableCell>{prayer.duration}</TableCell>
                          <TableCell>{prayer.location || '-'}</TableCell>
                          <TableCell className="max-w-xs truncate">{prayer.content || '-'}</TableCell>
                          <TableCell>{formatDateTimeKST(new Date(prayer.createdAt))}</TableCell>
                        </tr>
                      ))}
                    </tbody>
                  </>
            )}

            {activeTab === 'posts' && (
                  <>
                    <TableHeader>
                      <th 
                        className="px-2 py-2 text-left cursor-pointer hover:bg-gray-50 select-none"
                        onClick={() => handleSort('user')}
                      >
                        <div className="flex items-center gap-1">
                          사용자
                          {renderSortIcon('user')}
                        </div>
                      </th>
                      <th 
                        className="px-2 py-2 text-left cursor-pointer hover:bg-gray-50 select-none"
                        onClick={() => handleSort('content')}
                      >
                        <div className="flex items-center gap-1">
                          내용
                          {renderSortIcon('content')}
                        </div>
                      </th>
                      <th 
                        className="px-2 py-2 text-left cursor-pointer hover:bg-gray-50 select-none"
                        onClick={() => handleSort('verseReference')}
                      >
                        <div className="flex items-center gap-1">
                          말씀
                          {renderSortIcon('verseReference')}
                        </div>
                      </th>
                      <th 
                        className="px-2 py-2 text-left cursor-pointer hover:bg-gray-50 select-none"
                        onClick={() => handleSort('likes')}
                      >
                        <div className="flex items-center gap-1">
                          좋아요
                          {renderSortIcon('likes')}
                        </div>
                      </th>
                      <th 
                        className="px-2 py-2 text-left cursor-pointer hover:bg-gray-50 select-none"
                        onClick={() => handleSort('comments')}
                      >
                        <div className="flex items-center gap-1">
                          댓글
                          {renderSortIcon('comments')}
                        </div>
                      </th>
                      <th 
                        className="px-2 py-2 text-left cursor-pointer hover:bg-gray-50 select-none"
                        onClick={() => handleSort('createdAt')}
                      >
                        <div className="flex items-center gap-1">
                          생성일
                          {renderSortIcon('createdAt')}
                        </div>
                      </th>
                    </TableHeader>
                    <tbody>
                      {paginatedData.map((post: Post) => (
                        <tr key={post.id} className="border-b border-gray-100 hover:bg-gray-50">
                          <TableCell className="font-medium">{post.users?.name || '익명'}</TableCell>
                          <TableCell className="max-w-xs truncate">{post.content}</TableCell>
                          <TableCell className="max-w-xs truncate">{post.verseReference || '-'}</TableCell>
                          <TableCell>{post._count.likes}</TableCell>
                          <TableCell>{post._count.comments}</TableCell>
                          <TableCell>{formatDateTimeKST(new Date(post.createdAt))}</TableCell>
                        </tr>
                      ))}
                    </tbody>
                  </>
            )}

            {activeTab === 'meditations' && (
                  <>
                    <TableHeader>
                      <th 
                        className="px-2 py-2 text-left cursor-pointer hover:bg-gray-50 select-none"
                        onClick={() => handleSort('user')}
                      >
                        <div className="flex items-center gap-1">
                          사용자
                          {renderSortIcon('user')}
                        </div>
                      </th>
                      <th 
                        className="px-2 py-2 text-left cursor-pointer hover:bg-gray-50 select-none"
                        onClick={() => handleSort('verseReference')}
                      >
                        <div className="flex items-center gap-1">
                          말씀
                          {renderSortIcon('verseReference')}
                        </div>
                      </th>
                      <th 
                        className="px-2 py-2 text-left cursor-pointer hover:bg-gray-50 select-none"
                        onClick={() => handleSort('step1Complete')}
                      >
                        <div className="flex items-center gap-1">
                          단계1
                          {renderSortIcon('step1Complete')}
                        </div>
                      </th>
                      <th 
                        className="px-2 py-2 text-left cursor-pointer hover:bg-gray-50 select-none"
                        onClick={() => handleSort('step2Complete')}
                      >
                        <div className="flex items-center gap-1">
                          단계2
                          {renderSortIcon('step2Complete')}
                        </div>
                      </th>
                      <th 
                        className="px-2 py-2 text-left cursor-pointer hover:bg-gray-50 select-none"
                        onClick={() => handleSort('step3Complete')}
                      >
                        <div className="flex items-center gap-1">
                          단계3
                          {renderSortIcon('step3Complete')}
                        </div>
                      </th>
                      <th 
                        className="px-2 py-2 text-left cursor-pointer hover:bg-gray-50 select-none"
                        onClick={() => handleSort('step4Complete')}
                      >
                        <div className="flex items-center gap-1">
                          단계4
                          {renderSortIcon('step4Complete')}
                        </div>
                      </th>
                      <th 
                        className="px-2 py-2 text-left cursor-pointer hover:bg-gray-50 select-none"
                        onClick={() => handleSort('createdAt')}
                      >
                        <div className="flex items-center gap-1">
                          생성일
                          {renderSortIcon('createdAt')}
                        </div>
                      </th>
                    </TableHeader>
                    <tbody>
                      {paginatedData.map((meditation: Meditation) => (
                        <tr key={meditation.id} className="border-b border-gray-100 hover:bg-gray-50">
                          <TableCell className="font-medium">{meditation.users?.name || '익명'}</TableCell>
                          <TableCell className="max-w-xs truncate">{meditation.verseReference}</TableCell>
                          <TableCell>
                            <span className={`px-2 py-1 text-xs rounded ${
                              meditation.step1Complete ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                            }`}>
                              {meditation.step1Complete ? '완료' : '미완료'}
                            </span>
                          </TableCell>
                          <TableCell>
                            <span className={`px-2 py-1 text-xs rounded ${
                              meditation.step2Complete ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                            }`}>
                              {meditation.step2Complete ? '완료' : '미완료'}
                            </span>
                          </TableCell>
                          <TableCell>
                            <span className={`px-2 py-1 text-xs rounded ${
                              meditation.step3Complete ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                            }`}>
                              {meditation.step3Complete ? '완료' : '미완료'}
                            </span>
                          </TableCell>
                          <TableCell>
                            <span className={`px-2 py-1 text-xs rounded ${
                              meditation.step4Complete ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                            }`}>
                              {meditation.step4Complete ? '완료' : '미완료'}
                            </span>
                          </TableCell>
                          <TableCell>{formatDateTimeKST(new Date(meditation.createdAt))}</TableCell>
                        </tr>
                      ))}
                    </tbody>
                  </>
            )}

            {activeTab === 'crews' && (
                  <>
                    <TableHeader>
                      <th 
                        className="px-2 py-2 text-left cursor-pointer hover:bg-gray-50 select-none"
                        onClick={() => handleSort('name')}
                      >
                        <div className="flex items-center gap-1">
                          크루명
                          {renderSortIcon('name')}
                        </div>
                      </th>
                      <th className="px-2 py-2 text-left">크루장</th>
                      <th 
                        className="px-2 py-2 text-left cursor-pointer hover:bg-gray-50 select-none"
                        onClick={() => handleSort('description')}
                      >
                        <div className="flex items-center gap-1">
                          설명
                          {renderSortIcon('description')}
                        </div>
                      </th>
                      <th 
                        className="px-2 py-2 text-left cursor-pointer hover:bg-gray-50 select-none"
                        onClick={() => handleSort('members')}
                      >
                        <div className="flex items-center gap-1">
                          멤버 수
                          {renderSortIcon('members')}
                        </div>
                      </th>
                      <th className="px-2 py-2 text-center">승인</th>
                      <th className="px-2 py-2 text-center">보류</th>
                      <th className="px-2 py-2 text-center">거절</th>
                      <th className="px-2 py-2 text-left">공식</th>
                      <th 
                        className="px-2 py-2 text-left cursor-pointer hover:bg-gray-50 select-none"
                        onClick={() => handleSort('isActive')}
                      >
                        <div className="flex items-center gap-1">
                          상태
                          {renderSortIcon('isActive')}
                        </div>
                      </th>
                      <th 
                        className="px-2 py-2 text-left cursor-pointer hover:bg-gray-50 select-none"
                        onClick={() => handleSort('createdAt')}
                      >
                        <div className="flex items-center gap-1">
                          생성일
                          {renderSortIcon('createdAt')}
                        </div>
                      </th>
                      <th className="px-2 py-2 text-left">관리</th>
                    </TableHeader>
                    <tbody>
                      {paginatedData.map((crew: Crew) => (
                        <tr key={crew.id} className={`border-b border-gray-100 hover:bg-gray-50 ${selectedCrew?.id === crew.id ? 'bg-blue-50/50' : ''}`}>
                          <TableCell className="font-medium">
                            {crew.name}
                            {crew.isOfficial && <Star className="inline w-3 h-3 ml-1 text-yellow-500" />}
                          </TableCell>
                          <TableCell>
                            {crew.leader ? (
                              <div className="font-medium text-sm">{crew.leader.name}</div>
                            ) : (
                              <span className="text-gray-400 text-sm">-</span>
                            )}
                          </TableCell>
                          <TableCell className="max-w-[10rem] truncate">{crew.description || '-'}</TableCell>
                          <TableCell>{crew._count.members}</TableCell>
                          <TableCell className="text-center">
                            <span className="px-2 py-1 text-xs rounded bg-green-100 text-green-800 font-semibold">
                              {crew.requestStats?.approved || 0}
                            </span>
                          </TableCell>
                          <TableCell className="text-center">
                            <span className="px-2 py-1 text-xs rounded bg-yellow-100 text-yellow-800 font-semibold">
                              {crew.requestStats?.pending || 0}
                            </span>
                          </TableCell>
                          <TableCell className="text-center">
                            <span className="px-2 py-1 text-xs rounded bg-red-100 text-red-800 font-semibold">
                              {crew.requestStats?.rejected || 0}
                            </span>
                          </TableCell>
                          <TableCell>
                            <button
                              onClick={async (e) => {
                                e.stopPropagation();
                                await toggleCrewOfficial(crew.id, !crew.isOfficial);
                              }}
                              className={`px-2 py-1 text-xs rounded font-semibold ${
                                crew.isOfficial 
                                  ? 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200' 
                                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                              }`}
                              title={crew.isOfficial ? '공식 크루 해제' : '공식 크루로 지정'}
                            >
                              {crew.isOfficial ? '공식' : '일반'}
                            </button>
                          </TableCell>
                          <TableCell>
                            <span className={`px-2 py-1 text-xs rounded ${
                              crew.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                            }`}>
                              {crew.isActive ? '활성' : '비활성'}
                            </span>
                          </TableCell>
                          <TableCell>{formatDateTimeKST(new Date(crew.createdAt))}</TableCell>
                          <TableCell>
                            <div className="flex gap-1">
                              <button
                                className="px-2 py-1 text-xs bg-blue-500 text-white rounded hover:bg-blue-600"
                                onClick={async () => {
                                  setSelectedCrew(crew);
                                  await Promise.all([
                                    loadCrewMembers(crew.id),
                                    loadCrewJoinRequests(crew.id, 'PENDING')
                                  ]);
                                  setJoinRequestStatus('PENDING');
                                }}
                                title="크루 멤버 및 가입 신청 보기"
                              >
                                보기
                              </button>
                              <button
                                className="px-2 py-1 text-xs bg-emerald-500 text-white rounded hover:bg-emerald-600"
                                onClick={() => openCrewEditModal(crew)}
                                title="크루 정보 수정"
                              >
                                수정
                              </button>
                              {crew.isActive ? (
                                <button
                                  className="px-2 py-1 text-xs bg-red-500 text-white rounded hover:bg-red-600"
                                  onClick={() => handleDeleteCrew(crew)}
                                  title="크루 삭제 (비활성화)"
                                >
                                  삭제
                                </button>
                              ) : (
                                <button
                                  className="px-2 py-1 text-xs bg-red-700 text-white rounded hover:bg-red-800 font-bold"
                                  onClick={() => handlePermanentDeleteCrew(crew)}
                                  title="크루 완전 삭제 (복구 불가)"
                                >
                                  완전삭제
                                </button>
                              )}
                            </div>
                          </TableCell>
                        </tr>
                      ))}
                    </tbody>
                  </>
                )}
                  </table>
                </div>
              </div>
            )}

            {/* 크루 상세 관리 패널 */}
            {activeTab === 'crews' && selectedCrew && (
              <div className="bg-white rounded-lg shadow-sm border p-4 mt-3">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <h3 className="text-sm font-semibold text-gray-800">크루 관리: {selectedCrew.name}</h3>
                    <p className="text-xs text-gray-500">ID: {selectedCrew.id}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <select
                      value={joinRequestStatus}
                      onChange={async (e) => {
                        const v = e.target.value as 'PENDING' | 'APPROVED' | 'REJECTED';
                        setJoinRequestStatus(v);
                        await loadCrewJoinRequests(selectedCrew.id, v);
                      }}
                      className="px-2 py-1 text-xs border border-gray-300 rounded"
                    >
                      <option value="PENDING">신청(PENDING)</option>
                      <option value="APPROVED">승인(APPROVED)</option>
                      <option value="REJECTED">거절(REJECTED)</option>
                    </select>
                    <button
                      className="px-2 py-1 text-xs bg-gray-100 border border-gray-300 rounded"
                      onClick={async () => {
                        await Promise.all([
                          loadCrewMembers(selectedCrew.id),
                          loadCrewJoinRequests(selectedCrew.id, joinRequestStatus)
                        ]);
                      }}
                    >
                      새로고침
                    </button>
                    <button
                      className="px-2 py-1 text-xs bg-gray-100 border border-gray-300 rounded"
                      onClick={() => setSelectedCrew(null)}
                    >
                      닫기
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {/* 가입 신청 목록 */}
                  <div className="border rounded-md">
                    <div className="px-3 py-2 border-b flex items-center justify-between">
                      <h4 className="text-xs font-semibold text-gray-700">가입 신청 ({joinRequestStatus})</h4>
                      {isLoadingRequests && <span className="text-[10px] text-gray-500">로딩중...</span>}
                    </div>
                    <div className="p-3 space-y-2 max-h-72 overflow-auto">
                      {joinRequests.length === 0 ? (
                        <p className="text-xs text-gray-500">신청 내역이 없습니다.</p>
                      ) : (
                        joinRequests.map((req: any) => (
                          <div key={req.id} className="flex items-center justify-between p-2 border rounded">
                            <div>
                              <p className="text-xs font-medium text-gray-800">{req.user?.name}</p>
                              <p className="text-[11px] text-gray-500">{req.user?.email}</p>
                            </div>
                            <div className="flex items-center gap-2">
                              {joinRequestStatus === 'PENDING' ? (
                                <>
                                  <button
                                    className="px-2 py-1 text-[11px] bg-green-500 text-white rounded"
                                    onClick={() => handleProcessJoinRequest(selectedCrew.id, req.id, 'APPROVED')}
                                  >승인</button>
                                  <button
                                    className="px-2 py-1 text-[11px] bg-red-500 text-white rounded"
                                    onClick={() => handleProcessJoinRequest(selectedCrew.id, req.id, 'REJECTED')}
                                  >거절</button>
                                </>
                              ) : (
                                <span className="text-[11px] text-gray-500">{joinRequestStatus}</span>
                              )}
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>

                  {/* 멤버 목록 */}
                  <div className="border rounded-md">
                    <div className="px-3 py-2 border-b flex items-center justify-between">
                      <h4 className="text-xs font-semibold text-gray-700">멤버</h4>
                      <div className="flex items-center gap-2">
                        <div className="flex items-center gap-1 text-[11px]">
                          <label className="flex items-center gap-1 cursor-pointer">
                            <input
                              type="radio"
                              name="memberView"
                              checked={!showAllMembers}
                              onChange={() => setShowAllMembers(false)}
                            />
                            승인(레거시 포함)
                          </label>
                          <label className="flex items-center gap-1 cursor-pointer">
                            <input
                              type="radio"
                              name="memberView"
                              checked={showAllMembers}
                              onChange={() => setShowAllMembers(true)}
                            />
                            전체
                          </label>
                        </div>
                        <button
                          onClick={clearSelectedCrewMembers}
                          disabled={isMaintaining || !selectedCrew}
                          className="px-2 py-1 text-[11px] bg-red-500 text-white rounded disabled:opacity-50"
                          title="리더를 제외하고 멤버를 초기화하고 신청 내역을 삭제합니다."
                        >멤버 초기화</button>
                        {isLoadingMembers && <span className="text-[10px] text-gray-500">로딩중...</span>}
                      </div>
                    </div>
                    <div className="p-3 space-y-2 max-h-72 overflow-auto">
                      {(() => {
                        const list = showAllMembers ? (crewMembers.all || []) : (crewMembers.approved || []);
                        if (!list || list.length === 0) {
                          return <p className="text-xs text-gray-500">멤버가 없습니다.</p>;
                        }
                        return list.map((m: any) => {
                          const status = m.status ?? 'APPROVED';
                          return (
                            <div key={m.id} className="flex items-center justify-between p-2 border rounded">
                              <div>
                                <p className="text-xs font-medium text-gray-800">{m.user?.name}</p>
                                <p className="text-[11px] text-gray-500">{m.user?.email}</p>
                              </div>
                              <div className="flex items-center gap-2">
                                <span className="text-[11px] text-gray-600">{m.role}</span>
                                {showAllMembers && (
                                  <span className={`px-1.5 py-0.5 text-[10px] rounded ${
                                    status === 'APPROVED' ? 'bg-green-100 text-green-700' :
                                    status === 'PENDING' ? 'bg-yellow-100 text-yellow-700' :
                                    'bg-red-100 text-red-700'
                                  }`}>
                                    {status}
                                  </span>
                                )}
                              </div>
                            </div>
                          );
                        });
                      })()}
                    </div>
                  </div>
                </div>
              </div>
            )}

        {/* 데이터 없음 */}
        {activeTab !== 'stats' && !loading && paginatedData.length === 0 && (
          <div className="bg-white rounded-lg shadow-sm border p-6 text-center">
            <Database className="w-8 h-8 text-gray-400 mx-auto mb-2" />
            <p className="text-sm text-gray-600">데이터가 없습니다.</p>
          </div>
        )}
      </div>

      {/* 크루 수정 모달 */}
      {showCrewEditModal && editingCrew && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            {/* 모달 헤더 */}
            <div className="bg-gradient-to-r from-emerald-600 to-emerald-700 text-white px-6 py-4 rounded-t-2xl">
              <h2 className="text-xl font-bold">크루 정보 수정</h2>
              <p className="text-sm text-emerald-100 mt-1">크루의 기본 정보를 수정할 수 있습니다</p>
            </div>

            {/* 모달 본문 */}
            <div className="p-6 space-y-4">
              {/* 크루 이름 */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  크루 이름 <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={editCrewName}
                  onChange={(e) => setEditCrewName(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  placeholder="크루 이름을 입력하세요"
                />
              </div>

              {/* 크루 설명 */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  크루 설명
                </label>
                <textarea
                  value={editCrewDescription}
                  onChange={(e) => setEditCrewDescription(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 resize-none"
                  placeholder="크루에 대한 설명을 입력하세요"
                  rows={4}
                />
              </div>

              {/* 최대 멤버 수 */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  최대 멤버 수
                </label>
                <input
                  type="number"
                  value={editCrewMaxMembers}
                  onChange={(e) => setEditCrewMaxMembers(parseInt(e.target.value) || 50)}
                  min="1"
                  max="500"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
                <p className="text-xs text-gray-500 mt-1">
                  현재 멤버: {editingCrew._count.members}명
                </p>
              </div>

              {/* 크루 상태 정보 */}
              <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">크루 ID:</span>
                  <span className="font-mono text-xs text-gray-500">{editingCrew.id.slice(0, 8)}...</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">리더:</span>
                  <span className="font-medium">{editingCrew.leader?.name || '없음'}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">공식 크루:</span>
                  <span className={`px-2 py-0.5 text-xs rounded font-semibold ${
                    editingCrew.isOfficial 
                      ? 'bg-yellow-100 text-yellow-800' 
                      : 'bg-gray-100 text-gray-600'
                  }`}>
                    {editingCrew.isOfficial ? '공식' : '일반'}
                  </span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">상태:</span>
                  <span className={`px-2 py-0.5 text-xs rounded ${
                    editingCrew.isActive 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-red-100 text-red-800'
                  }`}>
                    {editingCrew.isActive ? '활성' : '비활성'}
                  </span>
                </div>
              </div>
            </div>

            {/* 모달 푸터 */}
            <div className="px-6 py-4 bg-gray-50 rounded-b-2xl flex gap-2 justify-end">
              <button
                onClick={() => {
                  setShowCrewEditModal(false);
                  setEditingCrew(null);
                }}
                className="px-4 py-2 text-sm font-semibold text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                취소
              </button>
              <button
                onClick={handleUpdateCrew}
                className="px-4 py-2 text-sm font-semibold text-white bg-emerald-600 rounded-lg hover:bg-emerald-700"
              >
                수정 완료
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;